import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { formatCurrency } from '../utils/currency';
import { useLanguage } from '../contexts/LanguageContext';
import { cle, useMessageTraduit } from '../utils/messageTraduit';
import EnteteEcran from '../components/commun/EnteteEcran';
import BarreFiltres from '../components/commun/BarreFiltres';
import PiedTableau from '../components/commun/PiedTableau';
import { useFormatageDates } from '../utils/dates';
import { raccourcisseurDeNumeros } from '../utils/numeroCommande';
import { PILE_CHASSE_FIXE } from '../theme/jetons';
import { useStock } from '../contexts/StockContext';
import DialogueDetailCommande from '../components/commandes/DialogueDetailCommande';
import ReceiptFormDialog from '../components/ReceiptFormDialog';

const OrderReception = ({ showSnackbar, currentStock, refreshUnreadNotifications }) => {
  const { t } = useLanguage();
  const { formaterDateSaisie } = useFormatageDates();
  const location = useLocation();
  const { refreshTrigger } = useStock();
  const [orders, setOrders] = useState([]);
  // ⚠️ PAGINATION LOCALE, ET C'EST UN CHOIX CONTRAINT. `getReceptionOrders()`
  // ne pagine pas côté serveur : il rend TOUT. Découper ici est donc une
  // amélioration d'affichage, pas de charge — la requête reste entière.
  // 🔴 Le vrai correctif serait de paginer la route ; il touche au backend et
  // sort du périmètre d'un lot d'interface. Consigné au ROADMAP.
  const [page, setPage] = useState(1);
  const [recherche, setRecherche] = useState('');
  const [fournisseurFiltre, setFournisseurFiltre] = useState('');
  const [parPage, setParPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useMessageTraduit();
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // 🔴 L'OUVERTURE VIENT DE L'URL, PAS D'UN `useState` — sinon le bouton
  // « précédent » recule dans l'historique sans rien fermer à l'écran.
  const navigate = useNavigate();
  const { id: commandeDetailleeId } = useParams();

  useEffect(() => {
    loadReceptionOrders();
  }, [currentStock?.id, refreshTrigger]);

  // Détecter si une commande spécifique est sélectionnée depuis la navigation
  useEffect(() => {
    if (location.state?.selectedOrderId) {
      const selectedOrderId = location.state.selectedOrderId;
      // Trouver la commande dans la liste et ouvrir le dialog de réception
      const order = orders.find(o => o.id === selectedOrderId);
      if (order) {
        handleCreateReceipt(order);
      }
      // Nettoyer l'état de navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, orders]);

  const loadReceptionOrders = async () => {
    try {
      setLoading(true);
      const response = await purchaseOrderService.getReceptionOrders();
      setOrders(response.orders || []);
    } catch (err) {
      setError(cle('reception.loadError'));
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // ⚠️ Ce code était RECOPIÉ À L'IDENTIQUE dans `OrderDetailView` : même
  // Chip, mêmes métadonnées, deux fichiers. Il passe désormais par le seul
  // composant qui peint un statut.
  // ⚠️ `getStatusChip` A DISPARU AVEC LA COLONNE « Statut ». Le rendu de statut
  // passait par `PastilleStatut`, le seul composant qui peint un statut — la
  // garde `H1` du dépôt le vérifie ; retirer l'appelant ne la met pas en défaut,
  // elle interdit les rendus PARALLÈLES, pas l'absence de rendu.

  // 🔴 RÉCEPTION N'AVAIT JAMAIS REÇU SA BARRE DE FILTRES — et ce n'est pas de la
  // mise en forme, c'est une fonction ABSENTE. C'est l'écran où l'on cherche une
  // commande PRÉCISE, livreur devant soi ; sans recherche, il faut la trouver à
  // l'œil dans une liste paginée.
  // ⚠️ MESURÉ : Réception n'était pas la seule oubliée de RU2 — la Comptabilité
  // ne l'a pas non plus. Trois écrans sur cinq l'ont (Produits, Commandes,
  // Fournisseurs). Consigné.
  // ⚠️ LE FILTRAGE EST LOCAL, sur la page chargée, comme sur les autres écrans.
  // Filtrer côté serveur demanderait un paramètre de plus à la route ; ce serait
  // le vrai correctif, et il sort d'un lot d'interface.
  const commandesFiltrees = (orders || []).filter((order) => {
    if (fournisseurFiltre && String(order.supplier_id) !== String(fournisseurFiltre)) {
      return false;
    }
    if (!recherche.trim()) return true;
    const q = recherche.trim().toLowerCase();
    return (order.order_number || '').toLowerCase().includes(q)
        || (order.supplier_name || '').toLowerCase().includes(q);
  });

  // ⚠️ LA LISTE DES FOURNISSEURS VIENT DES COMMANDES AFFICHÉES, pas d'un appel de
  // plus : proposer un fournisseur qui n'a aucune commande à recevoir donnerait
  // un filtre qui ne rend jamais rien. *Un filtre ne doit proposer que des
  // valeurs qui existent dans ce qu'il filtre.*
  const fournisseursPresents = [...new Map(
    (orders || []).filter((o) => o.supplier_id)
      .map((o) => [String(o.supplier_id), o.supplier_name])).entries()]
    .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));

  const raccourcirNumero = raccourcisseurDeNumeros(commandesFiltrees.map((o) => o.order_number));

  // ── LA BARRE D'AVANCEMENT — LE CŒUR DE L'ÉCRAN ───────────────────────────
  // 🔴 LES DEUX SOMMES SONT CALCULÉES ICI, PAS DEMANDÉES AU SERVEUR : la route
  // sert déjà chaque ligne avec `quantity` et `received_quantity`, et elle les
  // sert parce que le dialogue de réception en a besoin. Un agrégat de plus
  // aurait été une deuxième écriture de la même somme.
  // ⚠️ LA COULEUR EST CELLE DES DEUX STATUTS DE L'ÉCRAN — `info` pour `envoye`
  // comme pour `en_attente_complement` — pour que l'écran parle une seule
  // langue. Elle ne varie PAS avec l'avancement : la longueur dit le degré, la
  // couleur dirait une seconde chose à la même place.
  // ⚠️ ET LE FOND RESTE VISIBLE À 0 %, sinon « rien reçu » et « pas de barre »
  // se ressemblent — c'est-à-dire qu'on ne distinguerait pas une commande qui
  // n'a rien reçu d'une commande dont la donnée manque.
  const barreAvancement = (order) => {
    const lignes = order.items || [];
    const commande = lignes.reduce((n, l) => n + (Number(l.quantity) || 0), 0);
    const recu = lignes.reduce((n, l) => n + (Number(l.received_quantity) || 0), 0);
    const pourcent = commande > 0 ? Math.round((recu / commande) * 100) : 0;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={pourcent}
          color="info"
          sx={{ flex: 1, height: 8, borderRadius: 4 }}
        />
        <Typography variant="body2" fontWeight="medium" sx={{ whiteSpace: 'nowrap' }}>
          {recu} / {commande}
        </Typography>
      </Box>
    );
  };

  const handleCreateReceipt = (order) => {
    setSelectedOrder(order);
    setReceiptDialogOpen(true);
  };

  const handleViewDetails = (order) => navigate(`/order-reception/${order.id}`);

  const handleCloseDetail = () => navigate('/order-reception');

  // Le dialogue recharge la commande lui-même ; l'écran n'a plus qu'à remettre
  // sa liste à jour. Le double chargement d'avant venait de ce que l'écran
  // portait la commande ET la liste.
  const handleOrderUpdated = () => loadReceptionOrders();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ ml: 0, mr: 0, pl: 0, pr: 0 }}>
      {/* ⚠️ L'ICÔNE COLLÉE AU TITRE A DISPARU. Deux écrans sur cinq en
          portaient une (Réception, Comptabilité) et trois non : c'est
          précisément ce qui donnait l'impression de changer de logiciel en
          changeant d'onglet. L'icône vit déjà dans la barre latérale, où elle
          identifie l'écran ; la répéter dans le titre ne dit rien de plus. */}
      {/* ⚠️ « N commandes à recevoir » PLUTÔT QU'UN COMPTEUR NU. Le compteur de
          l'entête rend un nombre à côté d'un titre ; ici la phrase dit ce que le
          nombre COMPTE, et c'est la seule information de l'écran qu'on lit sans
          descendre. Les trois props d'`EnteteEcran` sont une limite voulue — on
          passe donc par `children`, prévu pour ça. */}
      <EnteteEcran titre={t('reception.title')}>
        <Typography variant="body2" color="text.secondary">
          {t('reception.ordersToReceive', { count: commandesFiltrees.length })}
        </Typography>
      </EnteteEcran>

      <DialogueDetailCommande
        key={commandeDetailleeId}
        id={commandeDetailleeId}
        ouvert={Boolean(commandeDetailleeId)}
        onFermer={handleCloseDetail}
        contexte={t('reception.title')}
        showSnackbar={showSnackbar}
        onCommandeModifiee={handleOrderUpdated}
      />

      {/* 🔴 LA BARRE EST HORS DE LA CONDITION DE LISTE VIDE, ET C'EST LE POINT
          QUI COMPTE. Placée dans la branche « il y a des résultats », elle
          DISPARAÎT dès qu'une recherche n'en rend aucun — et l'on ne peut plus
          corriger sa recherche puisque le champ n'est plus là. *Un filtre qui
          s'efface avec ce qu'il filtre est un piège sans sortie.* */}


      <BarreFiltres
        valeur={recherche}
        onChange={setRecherche}
        libelle={t('reception.search')}
      >
        <TextField
          select
          size="small"
          label={t('orders.supplier')}
          value={fournisseurFiltre}
          onChange={(e) => setFournisseurFiltre(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t('reception.allSuppliers')}</MenuItem>
          {fournisseursPresents.map(([id, nom]) => (
            <MenuItem key={id} value={id}>{nom}</MenuItem>
          ))}
        </TextField>
      </BarreFiltres>

      {/* ⚠️ DEUX VIDES DIFFÉRENTS. « Aucune commande à recevoir » et « aucun
          résultat pour cette recherche » se ressemblent à l'écran et n'appellent
          pas le même geste : le premier dit qu'il n'y a rien à faire, le second
          qu'il faut corriger sa recherche. */}
      {commandesFiltrees.length === 0 ? (
        <Card>
          <CardContent>
            <Alert severity="info">
              {(orders || []).length === 0
                ? t('reception.noPendingOrders')
                : t('reception.noSearchResult')}
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            ml: 0, 
            mr: 0,
            '& .MuiPaper-root': {
              padding: 0,
              margin: 0,
            }
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {/* 🔴 LA COLONNE « STATUT » S'EN VA, ET C'EST MESURÉ, PAS UN CHOIX
                    D'ESPACE. Dans CET écran, le statut est exactement dérivable de
                    l'avancement : le serveur pose `envoye` quand rien n'est reçu et
                    `en_attente_complement` dès qu'une unité l'est (`purchase_orders.py`,
                    l. 678 et 789), et `livre` n'y entre jamais. La colonne disait donc
                    ce que la barre MONTRE.
                    ⚠️ Et elle le disait mal : les deux statuts partagent la couleur
                    `info`, si bien que deux pastilles bleues ne se distinguaient qu'en
                    LISANT leur libellé. *Un signal qui demande d'être lu n'est pas un
                    signal de balayage.* */}
                <TableCell>{t('orders.orderNumber')}</TableCell>
                <TableCell>{t('orders.supplier')}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>{t('reception.progress')}</TableCell>
                <TableCell align="right">{t('orders.total')}</TableCell>
                <TableCell>{t('products.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {commandesFiltrees.slice((page - 1) * parPage, page * parPage).map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold"
                                title={order.order_number || ''}
                                sx={{ fontFamily: PILE_CHASSE_FIXE }}>
                      {raccourcirNumero(order.order_number)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formaterDateSaisie(order.order_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>{order.supplier_name}</TableCell>
                  <TableCell>{barreAvancement(order)}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(order.total_final || order.total_amount, order.currency || 'EUR')}
                  </TableCell>
                  <TableCell>
                    {/* Un bouton TEXTE : « + » ne dit pas ce qu'il ajoute, et
                        c'est le geste principal de l'écran. */}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleCreateReceipt(order)}
                    >
                      {t('reception.receive')}
                    </Button>
                     <IconButton
                       color="info"
                       onClick={() => handleViewDetails(order)}
                       title={t('reception.viewDetails')}
                     >
                       <VisibilityIcon />
                     </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 🔴 CET ÉCRAN N'AVAIT AUCUNE PAGINATION. Une liste longue s'affichait
          entière, sans que rien n'indique combien de commandes attendaient une
          réception. Réception et Comptabilité étaient les deux seuls dans ce
          cas — les trois autres écrans en avaient une. */}
      {commandesFiltrees.length > 0 && (
        <PiedTableau
          total={commandesFiltrees.length}
          page={page}
          pages={Math.ceil(commandesFiltrees.length / parPage)}
          parPage={parPage}
          onPage={setPage}
          onParPage={(n) => { setParPage(n); setPage(1); }}
          libelleTotal={`${commandesFiltrees.length} ${t('reception.title').toLowerCase()}`}
          libelleParPage={t('common.perPage')}
        />
      )}

      <ReceiptFormDialog
        order={selectedOrder}
        open={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        onSuccess={() => {
          if (refreshUnreadNotifications) refreshUnreadNotifications();
          loadReceptionOrders();
        }}
        showSnackbar={showSnackbar}
      />

      
    </Box>
  );
};

export default OrderReception;
